<template>
  <img id="partnerize-pixel"
       v-if="show"
       :src="srcUrl"
       width="1"
       height="2"
       alt=""
  />
</template>

<script>
  import { get, call } from 'vuex-pathify';
  import moment from 'moment';

  export default {
    name: 'partnerize-pixel',
    props: {
      booking: {
        type: Object,
        required: true,
        default: () => null,
      },
      product: {
        type: Object,
        required: true,
        default: () => null,
      }
    },
    data() {
      return {
        show: false,
        src: null,
        campaign: null,
        conversionRef: null,
        country: 'GB',
        currency: 'GBP',
        customerType: null,
        voucher: null,
        items: [],
        item: {
          category: null,
          value: null,
          salesRevenue: null,
          quantity: 1,
          arrivalDate: null,
          exitDate: null,
          carParkIdNumber: null,
          airportName: null,
          stayDuration: null,
          grossValue: null,
          originalPrice: null,
          discount: null,
          departureDate: null,
          bookingDate: null,
          terminal: null,
          flightNumber: null,
        },
      }
    },
    computed: {
      partnerizeUrl() { return process.env.VUE_APP_PARTNERIZE_URL; },
      partnerizeCampaign() { return process.env.VUE_APP_PARTNERIZE_CAMPAIGN; },
      srcUrl() { return this.src; },
      quote: get("quote"),
      results: get("results"),
      cart: get("cart"),
      auth: get("auth"),
      payment: get("payment"),
    },
    async mounted() {
      // Format data to match expected partnerize structure
      if (await this.preparePartnerizeStructures()) {

        // Combine partly created structure to one partnerize src URL
        await this.combineSrcUrl();
      }
    },
    methods: {
      getBooking: call("payment/confirmation"),
      async preparePartnerizeStructures() {
        // Campaign
        if (!(this.partnerizeCampaign !== null && this.partnerizeCampaign.length > 0))
          return false;

        this.campaign = this.partnerizeCampaign;
        this.conversionRef = this.payment.confirmation.details.transaction.transaction_id;

        // Customer Type & Voucher
        this.customerType = (this.auth.loggedIn) ? 'existing' : 'new';
        this.voucher = this.quote.promo;

        // Map single bookings to partnerize structures and hold in items property
        await this.mapItems();

        // Returns true only if previous steps correctly handled
        return true;
      },
      async mapItems() {
        let addonsValue = 0;
        await this.booking.details.bookings.addon.every(sale => addonsValue += sale.booking.sale_price);
        await this.booking.details.bookings.airportparking.every(async booking => await this.convertParkings(booking, addonsValue));
        await this.booking.details.bookings.airporthotel.every(async booking => await this.convertHotels(booking, addonsValue)); // @TODO - in future
        // this.booking.details.bookings.airportlounge.every(async booking => await this.convertLounges(booking, addonsValue)); // @TODO - in future
      },
      async convertParkings(booking, addonsValue) {
        let item = Object.assign({}, this.item);
        const departureDate = moment(booking.booking.departure_date_time).format('DD-MM-YYYY');
        const returnDate = moment(booking.booking.return_date_time).format('DD-MM-YYYY');
        const bookingDate = moment(booking.booking.booking_date).format('DD-MM-YYYY');

        item.category = this.product ? this.product.park_name : 'airportparking';
        item.value = parseFloat(booking.booking.sale_price - (booking.booking.sale_price / 6)).toFixed(2);
        item.salesRevenue = (parseFloat(booking.booking.commission) / 1.2).toFixed(2);
        item.arrivalDate = departureDate;
        item.exitDate = returnDate;
        item.carParkIdNumber = booking.booking.product_id;
        item.airportName = this.results.airport.airport_name;
        item.stayDuration = booking.booking.days;
        item.grossValue = booking.booking.full_price + addonsValue;
        item.originalPrice = booking.booking.full_price;
        item.discount = booking.booking.discount;
        item.departureDate = departureDate;
        item.bookingDate = bookingDate;
        item.terminal = booking.booking.outbound_terminal || "";
        item.flightNumber = booking.booking.outbound_flight || "";
        this.items.push(item);
      },
      async convertHotels(booking, addonsValue) {
        let item = Object.assign({}, this.item);
        const departureDate = moment(booking.booking.stay_on).format('DD-MM-YYYY');
        const bookingDate = moment(booking.booking.booking_date).format('DD-MM-YYYY');

        item.category = this.product ? this.product.hotel_name : 'airporthotel';
        item.value = parseFloat(booking.booking.sale_price - (booking.booking.sale_price / 6)).toFixed(2);
        item.salesRevenue = (parseFloat(booking.booking.commission) / 1.2).toFixed(2);
        item.arrivalDate = departureDate;
        item.exitDate = null;
        item.carParkIdNumber = booking.booking.product_id;
        item.airportName = this.results.airport.airport_name;
        item.stayDuration = booking.booking.days;
        item.grossValue = booking.booking.full_price + addonsValue;
        item.originalPrice = booking.booking.full_price;
        item.discount = booking.booking.discount;
        item.departureDate = departureDate;
        item.bookingDate = bookingDate;
        item.terminal = booking.booking.outbound_terminal || "";
        item.flightNumber = booking.booking.outbound_flight || "";
        this.items.push(item);
      },
      // async convertLounges(booking, addonsValue) {
      //    @TODO - in future
      // },
      async combineSrcUrl() {
        // Set up partnerize URL base path
        this.src = await this.partnerizeUrl;

        // Append general campaign info
        await this.appendGeneralInfo();

        // Append every basket item at the end
        if (this.items.length > 0)
          await this.items.forEach(async itm => {
            let booking = await this.appendItemsInfo(itm)
            return this.src += booking;
          });

        // Render the image tag now that processing data has finished
        this.show = true;
      },
      async appendGeneralInfo() {
        this.src += `campaign:${this.campaign}/`;
        this.src += `conversionref:${this.conversionRef}/`;
        this.src += `country:${this.country}/`;
        this.src += `currency:${this.currency}/`;
        this.src += `customertype:${this.customerType}/`;
        this.src += `voucher:${this.voucher}/`;
      },
      async appendItemsInfo(item) {
        let itemUrl = '[';

        // Append every existing item param
        await Object.keys(this.item).forEach(key => {
          if (key in item && item[key] !== null) itemUrl += `${this.toSnakeCase(key)}:${item[key]}/`
        });

        // Replace last char `/` with closing `]`
        itemUrl = itemUrl.substring(0, itemUrl.length - 1);
        itemUrl += ']';

        return itemUrl;
      },
      resetGeneral() {
        this.src = null;
        this.campaign = null;
        this.conversionRef = null;
        this.country = 'GB';
        this.currency = 'GBP';
        this.customerType = null;
        this.voucher = null;
        this.items = [];
      },
      toSnakeCase(str) {
        return str && str
          .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
          .map(x => x.toLowerCase())
          .join('_')
      }
    },
  }
</script>
